'use client'

import type { ComponentType } from 'react'
import { useExtracted } from 'next-intl'
import { Download } from '@/components/ui/icons'
import { AppleGlyph, WindowsGlyph } from '@/components/ui/platformGlyphs'
import { DESKTOP_DOWNLOADS, DESKTOP_FILE_SIZES } from '@/lib/desktop-releases'

// Platform metadata kept in plain JS (not JSX) so the file format / size detail
// isn't flagged as user-facing copy; labels still go through t() at the call site.
const MAC = { href: DESKTOP_DOWNLOADS.macArm, glyph: AppleGlyph, detail: `.dmg · ${DESKTOP_FILE_SIZES.macArm}` }
const WIN = { href: DESKTOP_DOWNLOADS.windows, glyph: WindowsGlyph, detail: `.exe · ${DESKTOP_FILE_SIZES.windows}` }

/**
 * "Get the desktop app" entry cards for the Learn landing — same shape and size
 * as the Docs/Tutorials tiles, so they align column-for-column above them. Each
 * links straight to the latest `.dmg` / `.exe`, so a click starts the download.
 */
export function LearnDownloadButtons() {
  const t = useExtracted()
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <DownloadCard {...MAC} label={t('Download for Mac')} />
      <DownloadCard {...WIN} label={t('Download for Windows')} />
    </div>
  )
}

function DownloadCard({
  href,
  glyph: Glyph,
  label,
  detail,
}: {
  href: string
  glyph: ComponentType<{ className?: string }>
  label: string
  detail: string
}) {
  return (
    <a
      href={href}
      className="group flex flex-col rounded-lg border border-border/40 bg-card/20 p-5 transition-all hover:border-border/60 hover:bg-card/40"
    >
      <span className="flex items-start justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/40 text-muted-foreground transition-colors group-hover:text-foreground">
          <Glyph className="h-[18px] w-[18px]" />
        </span>
        <Download className="mt-1 h-4 w-4 text-muted-foreground/50 transition-colors group-hover:text-foreground" />
      </span>
      <span className="mt-3 text-base font-semibold text-foreground">{label}</span>
      <span className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</span>
    </a>
  )
}
